import { faCaretDown } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import { LocationDescriptor } from "history";
import React from "react";

import { Link } from "@/mk2/components/Link";
import useEventCallback from "@/mk2/hooks/useEventCallback";
import { getLabel } from "@/mk/bazaar/product/categories";

import styles from "./CategoryFacetOption.mscss";

export type CategoryKey = string;

export interface OwnProps {
    className?: string;
    category: CategoryKey;
    to?: LocationDescriptor;
    selected?: boolean;
    dimmed?: boolean;
    hasSubcategories?: boolean;
    opened?: boolean;
    count?: number;

    onSelect?(category: CategoryKey);
}

type Props = OwnProps;

const CategoryFacetOption: React.FunctionComponent<Props> = ({
    className,
    category,
    to,
    selected = false,
    dimmed = false,
    hasSubcategories = false,
    opened = false,
    count = null,
    onSelect,
}) => {
    const onSelectCallback = useEventCallback(
        (event: React.MouseEvent<HTMLAnchorElement | HTMLDivElement>) => {
            event.preventDefault();

            if (onSelect && count !== 0) {
                // Disable if count === 0
                onSelect(category);
            }
        },
        [onSelect, category],
    );

    const content = (
        <>
            {dimmed && <div className={styles.CategoryFacetOption__dimmed} />}
            <div
                className={cx(
                    styles.CategoryFacetOption__icon,
                    styles[`CategoryFacetOption__icon--${category}`],
                    count === 0 && styles[`CategoryFacetOption__icon--zeroHits`],
                )}
            />
            <div className={styles.CategoryFacetOption__info}>
                <div
                    className={cx(
                        styles.CategoryFacetOption__label,
                        selected && styles["CategoryFacetOption__label--selected"],
                    )}
                >
                    {getLabel(category)}
                </div>
                {count > 0 && <div className={styles.CategoryFacetOption__count}>{count}</div>}
            </div>
            {hasSubcategories && (
                <div className={styles.CategoryFacetOption__caret}>
                    <FontAwesomeIcon icon={faCaretDown} />
                </div>
            )}
            {count === 0 && <div className={styles.CategoryFacetOption__zeroOverlay} />}
        </>
    );

    return (
        <div
            className={cx(
                styles.CategoryFacetOption,
                className,
                opened && styles["CategoryFacetOption--opened"],
                count !== 0 && styles["CategoryFacetOption--clickable"],
            )}
        >
            {to && count !== 0 && !hasSubcategories ? (
                <Link
                    to={to}
                    className={styles.CategoryFacetOption__container}
                    onClick={onSelectCallback}
                >
                    {content}
                </Link>
            ) : (
                <div className={styles.CategoryFacetOption__container} onClick={onSelectCallback}>
                    {content}
                </div>
            )}
        </div>
    );
};

CategoryFacetOption.displayName = "CategoryFacetOption";

export default React.memo<Props>(CategoryFacetOption);
